import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import {
  buildResponseValidationReport,
  type KyRequestOptions,
} from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type ContactLink } from '../types';
import { zodContactLinkSchema } from '../validations';

/**
 * Creates a contact link for the specified data exchange.
 *
 * The API generates a registration link associated with the given `dataExchangeId`.
 * The response includes metadata about the link such as its validity and access history.
 *
 * Validates strictly: cached as trusted data and dereferenced immediately to
 * build the mailto link.
 *
 * @param dataExchangeId - The unique identifier of the data exchange.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The created contact link data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/add_contact_link_data_exchange_contact_link_post
 */
export const addContactLink = async (
  dataExchangeId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.SUPPLIER.ADD_CONTACT_LINK(), {
      ...options,
      json: { dataExchangeId },
    })
    .json();

  const parsed = zodContactLinkSchema.safeParse(response);

  if (!parsed.success) {
    log.responseValidation({
      ...buildResponseValidationReport(parsed.error.issues),
      key: 'add-contact-link',
      message: 'add contact link response has validation errors',
    });

    throw new Error(
      'Add contact link response did not match the expected shape',
    );
  }

  return parsed.data satisfies ContactLink;
};
